import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const DreVisaoGeral = () => {
  const anoAtual = Number(format(new Date(), 'yyyy'));
  const [carregando, setCarregando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);
  const [filtros, setFiltros] = useState({
    visao: 'mensal',
    ano_base: anoAtual,
    regime: 'competencia',
    tipo_valor: 'periodo',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
  });
  const [dados, setDados] = useState({
    anos_disponiveis: [anoAtual],
    colunas: [],
    linhas: [],
    resumo_por_coluna: [],
  });
  const [paisRecolhidos, setPaisRecolhidos] = useState(new Set());

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros da DRE visao geral:', error);
    }
  };

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/dre-visao-geral', { params: filtros });
      const payload = response.data || {};
      setDados({
        anos_disponiveis: Array.isArray(payload.anos_disponiveis) && payload.anos_disponiveis.length > 0 ? payload.anos_disponiveis : [anoAtual],
        colunas: Array.isArray(payload.colunas) ? payload.colunas : [],
        linhas: Array.isArray(payload.linhas) ? payload.linhas : [],
        resumo_por_coluna: Array.isArray(payload.resumo_por_coluna) ? payload.resumo_por_coluna : [],
      });
    } catch (error) {
      console.error('Erro ao carregar DRE visao geral:', error);
      toast.error(error.response?.data?.error || 'Erro ao carregar DRE visao geral');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarDados();
  }, [filtros]);

  const formatarValor = (valor) => {
    const n = Number(valor);
    const safe = Number.isFinite(n) ? n : 0;
    return safe.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const resumoGeral = useMemo(() => {
    if (dados.resumo_por_coluna.length === 0) {
      return { receitas: 0, despesas: 0, resultado: 0 };
    }

    if (filtros.tipo_valor === 'acumulado') {
      const last = dados.resumo_por_coluna[dados.resumo_por_coluna.length - 1] || {};
      return {
        receitas: Number(last.receitas || 0),
        despesas: Number(last.despesas || 0),
        resultado: Number(last.resultado || 0),
      };
    }

    let receitas = 0;
    let despesas = 0;
    dados.resumo_por_coluna.forEach((item) => {
      receitas += Number(item?.receitas || 0);
      despesas += Number(item?.despesas || 0);
    });
    const resultado = receitas - despesas;
    return { receitas, despesas, resultado };
  }, [dados.resumo_por_coluna, filtros.tipo_valor]);

  const childCountByParentId = useMemo(() => {
    const map = new Map();
    (dados.linhas || []).forEach((linha) => {
      const parentId = linha?.parent_id || null;
      if (!parentId) return;
      map.set(parentId, (map.get(parentId) || 0) + 1);
    });
    return map;
  }, [dados.linhas]);

  const linhasComHierarquia = useMemo(() => {
    return (dados.linhas || []).map((linha) => {
      const nivelRaw = Number(linha?.nivel);
      const nivelFromCodigo = Math.max(1, String(linha?.codigo || '').split('.').filter(Boolean).length);
      const nivel = Number.isFinite(nivelRaw) && nivelRaw > 0 ? nivelRaw : nivelFromCodigo;
      const isPai = (childCountByParentId.get(linha.plano_conta_id) || 0) > 0;
      const isFilha = Boolean(linha?.parent_id) || nivel > 1;
      return {
        ...linha,
        nivel,
        is_pai: isPai,
        is_filha: isFilha,
      };
    });
  }, [dados.linhas, childCountByParentId]);

  const parentIdById = useMemo(() => {
    const map = new Map();
    linhasComHierarquia.forEach((linha) => {
      map.set(linha.plano_conta_id, linha.parent_id || null);
    });
    return map;
  }, [linhasComHierarquia]);

  useEffect(() => {
    const parentIdsValidos = new Set(
      linhasComHierarquia.filter((linha) => linha.is_pai).map((linha) => linha.plano_conta_id)
    );
    setPaisRecolhidos((old) => new Set([...old].filter((id) => parentIdsValidos.has(id))));
  }, [linhasComHierarquia]);

  const linhasVisiveis = useMemo(() => {
    const hasPaiRecolhidoNoCaminho = (linha) => {
      let atual = linha.parent_id || null;
      while (atual) {
        if (paisRecolhidos.has(atual)) return true;
        atual = parentIdById.get(atual) || null;
      }
      return false;
    };
    return linhasComHierarquia.filter((linha) => !hasPaiRecolhidoNoCaminho(linha));
  }, [linhasComHierarquia, parentIdById, paisRecolhidos]);

  const alternarPai = (planoContaId) => {
    setPaisRecolhidos((old) => {
      const next = new Set(old);
      if (next.has(planoContaId)) next.delete(planoContaId);
      else next.add(planoContaId);
      return next;
    });
  };

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">DRE Visao Geral</h1>
          <p className="text-gray-600">Matriz dinamica mensal/anual por contas da DRE</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="form-label">Visao</label>
              <select
                className="form-select"
                value={filtros.visao}
                onChange={(e) => setFiltros((old) => ({ ...old, visao: e.target.value }))}
              >
                <option value="mensal">Mensal</option>
                <option value="anual">Anual</option>
              </select>
            </div>

            <div>
              <label className="form-label">Ano Base</label>
              <select
                className="form-select"
                value={filtros.ano_base}
                disabled={filtros.visao !== 'mensal'}
                onChange={(e) => setFiltros((old) => ({ ...old, ano_base: Number(e.target.value) }))}
              >
                {[...new Set([anoAtual, ...(dados.anos_disponiveis || [])])]
                  .sort((a, b) => b - a)
                  .map((ano) => (
                    <option key={ano} value={ano}>
                      {ano}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label className="form-label">Regime</label>
              <select
                className="form-select"
                value={filtros.regime}
                onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}
              >
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>

            <div>
              <label className="form-label">Tipo de Valor</label>
              <select
                className="form-select"
                value={filtros.tipo_valor}
                onChange={(e) => setFiltros((old) => ({ ...old, tipo_valor: e.target.value }))}
              >
                <option value="periodo">Periodo</option>
                <option value="acumulado">Acumulado</option>
              </select>
            </div>

            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Receitas</p>
            <p className="text-2xl font-bold text-success-600">R$ {formatarValor(resumoGeral.receitas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Despesas</p>
            <p className="text-2xl font-bold text-danger-600">R$ {formatarValor(resumoGeral.despesas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Resultado</p>
            <p className={`text-2xl font-bold ${resumoGeral.resultado >= 0 ? 'text-success-600' : 'text-danger-600'}`}>
              R$ {formatarValor(resumoGeral.resultado)}
            </p>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold">Matriz DRE</h3>
            <p className="text-xs text-gray-500">Use o botao +/- para expandir ou recolher contas pai.</p>
          </div>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta DRE</th>
                  {dados.colunas.map((col) => (
                    <th key={col.key}>{col.label}</th>
                  ))}
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {linhasVisiveis.map((linha) => {
                  const paiRecolhido = paisRecolhidos.has(linha.plano_conta_id);
                  const compactStyle = linha.is_filha ? { paddingTop: '0.32rem', paddingBottom: '0.32rem' } : undefined;
                  const linhaTipoClass = linha.tipo === 'receita'
                    ? linha.is_pai ? 'bg-emerald-100/60 border-l-4 border-emerald-300' : 'bg-emerald-50/30 border-l-4 border-emerald-200'
                    : linha.is_pai ? 'bg-rose-100/60 border-l-4 border-rose-300' : 'bg-rose-50/30 border-l-4 border-rose-200';

                  return (
                    <tr key={linha.plano_conta_id} className={`${linhaTipoClass} ${linha.is_filha ? 'text-[12px]' : ''}`}>
                      <td style={compactStyle}>
                        <div className="max-w-xs flex items-center gap-2" style={{ paddingLeft: `${Math.max(0, (Number(linha.nivel || 1) - 1) * 14)}px` }}>
                          {linha.is_pai && (
                            <button
                              type="button"
                              className="h-5 w-5 shrink-0 rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                              onClick={() => alternarPai(linha.plano_conta_id)}
                              title={paiRecolhido ? 'Expandir' : 'Recolher'}
                            >
                              {paiRecolhido ? '+' : '-'}
                            </button>
                          )}
                          <div>
                            <p className={linha.is_pai ? 'font-semibold text-gray-900' : 'font-medium text-gray-800'}>
                              {linha.codigo} - {linha.descricao}
                            </p>
                            <p className="text-xs text-gray-500">{linha.dre_grupo || 'Sem grupo'} - {linha.tipo}</p>
                          </div>
                        </div>
                      </td>
                      {dados.colunas.map((col) => (
                        <td
                          key={`${linha.plano_conta_id}-${col.key}`}
                          style={compactStyle}
                          className={`${linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'} ${linha.is_pai ? 'font-semibold' : ''}`}
                        >
                          R$ {formatarValor(linha.valores?.[col.key])}
                        </td>
                      ))}
                      <td
                        style={compactStyle}
                        className={`font-semibold ${linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'} ${linha.is_pai ? 'bg-white/40' : ''}`}
                      >
                        R$ {formatarValor(linha.total)}
                      </td>
                    </tr>
                  );
                })}
                {linhasVisiveis.length === 0 && !carregando && (
                  <tr>
                    <td colSpan={(dados.colunas?.length || 0) + 2} className="text-center text-gray-500 py-8">
                      Nenhum dado encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DreVisaoGeral;
